// src/components/profesional/EditarFormulario.tsx
import { useEffect, useState, useRef } from "react";
import EditaPerfilForm from "./EditaPerfilForm"; // Formulario principal de edición de perfil
import BotonTres, { ButtonPopup } from "../../ui/ButtonPopup"; // Botón reutilizable
import EditarPerfilRedesSociales from "./EditaPerfilRedesSocialesForm"; // Formulario de redes sociales
import EditarPerfilTelefono from "./EditaPerfilTelefonosForm"; // Formulario de teléfonos
import EditarPerfilLogoForm from "./EditarPerfilLogoForm"; // Formulario para logo
import {
  EditarPerfilFormProps,
  Horario,
  ImagenesTrabajos,
  Perfil_Profesional,
  RedSocial,
  Telefono,
  Usuario,
} from "@/types"; // Tipos de @/typescript
import EditaImagesProfesional from "./EditaImagesProfesionalForm"; // Gestión de imágenes de trabajos
import {
  eliminarImagenEnServidor,
  subirLogo,
  subirImagenTrabajo,
} from "../../utils/subirImagen"; // Funciones para subir/eliminar imágenes
import { PLATFORM_LOGOS } from "../../utils/SocialConfig"; // Logos de redes sociales
import EditarHorariosProfesionalForm from "./EditarHorariosProfesionalForm";
// URL base del backend desde .env
import { safeFetch } from "@/api/safeFetch";
import { useAuthToken } from "@/hooks/useAuthToken";
import { useNavigate } from "react-router-dom";
import Alerta from "@/components/alert/Alerta";
import { TextareaComun } from "@/components/ui";

// Componente principal de edición de perfil profesional
const EditarFormulario: React.FC<EditarPerfilFormProps> = ({
  datosPerfilProfesional,
}) => {
  // Convierte el ID de perfil a string
  /*  const perfilId = datosPerfilProfesional.perfil_profesional_id ?? 0; */
  // Estado para errores y mensajes de éxito
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Logo: estado inicial y control de cambios
  const initialLogoPath = useRef<string | undefined>(
    datosPerfilProfesional?.foto_perfil || undefined,
  );
  const [currentLogoPath, setCurrentLogoPath] = useState<string | undefined>(
    initialLogoPath.current,
  );
  const [logoPreview, setLogoPreview] = useState<string>();
  const [logoPendingFiles, setLogoPendingFiles] = useState<File[]>([]);
  const [logoDeleted, setLogoDeleted] = useState(false);
  const perfilId = datosPerfilProfesional?.perfil_profesional_id!;
  const profesionalId = datosPerfilProfesional?.profesional_id;
  const profesionId = datosPerfilProfesional?.profesion_id;
  const [horarios, setHorarios] = useState<Horario[]>(
    datosPerfilProfesional?.horarios ?? [],
  );
  const { usuarioId } = useAuthToken();
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const token = localStorage.getItem("token") || ""; // 💥 AÑADE ESTO
  const navigate = useNavigate();

  /* Landing */
  const [landingCreada, setLandingCreada] = useState(false);
  const [landingPublica, setLandingPublica] = useState(false);

  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: "success" | "error" | "info" | "warning";
  } | null>(null);

  // Estado del formulario principal
  const [formEditarPerfil, setFormEditarPerfil] = useState<Perfil_Profesional>({
    perfil_profesional_id: datosPerfilProfesional?.perfil_profesional_id,
    usuario_id: datosPerfilProfesional?.usuario_id,
    profesion_id: datosPerfilProfesional?.profesion_id,
    profesional_id: datosPerfilProfesional?.profesional_id,

    nombre_empresa: datosPerfilProfesional?.nombre_empresa,
    nombre_comercial: datosPerfilProfesional?.nombre_comercial || "",
    dniNie: datosPerfilProfesional?.dniNie,
    cif: datosPerfilProfesional?.cif,
    correo_perfil: datosPerfilProfesional?.correo_perfil,
    url_web: datosPerfilProfesional?.url_web,

    // 👇 DATOS DEL AFILIADO
    codigo_afiliado: datosPerfilProfesional?.codigo_afiliado,
    puntos: datosPerfilProfesional?.puntos ?? 0,

    direccion: {
      calle: datosPerfilProfesional?.direccion?.calle || "",
      localidad: datosPerfilProfesional?.direccion?.localidad || "",
      provincia: datosPerfilProfesional?.direccion?.provincia || "",
      comunidad: datosPerfilProfesional?.direccion?.comunidad || "",
      pais: "España",
      codigo_postal: datosPerfilProfesional?.direccion?.codigo_postal || 0,
    },
  });

  // Errores de validación
  const [, setErrors] = useState({
    nombre_empresa: false,
    correo_perfil: false,
    url_web: false,
    direccion: {
      calle: false,
      localidad: false,
      provincia: false,
      pais: false,
      codigo_postal: false,
    },
  });

  // Función para validar campos obligatorios
  const validateForm = () => {
    const newErrors = {
      nombre_empresa: !formEditarPerfil.nombre_empresa,
      correo_perfil: !formEditarPerfil.correo_perfil,
      url_web: false,
      direccion: {
        calle: !formEditarPerfil.direccion?.calle,
        localidad: !formEditarPerfil.direccion?.localidad,
        provincia: !formEditarPerfil.direccion?.provincia,
        pais: !formEditarPerfil.direccion?.pais,
        codigo_postal: !formEditarPerfil.direccion?.codigo_postal,
      },
    };

    setErrors(newErrors);

    // Retorna true si no hay errores
    return !Object.values(newErrors).some((val) =>
      typeof val === "boolean" ? val : Object.values(val).some((v) => v),
    );
  };

  // Estado y funciones para teléfonos
  const [phones, setPhones] = useState<Telefono[]>(
    (datosPerfilProfesional?.telefonos ?? []).map((phone) => ({
      telefono_id: phone.telefono_id,
      numero_telefono: phone.numero_telefono,
    })),
  );
  const handlePhoneChange = (updatedPhones: Telefono[]) =>
    setPhones(updatedPhones);

  // Estado y funciones para redes sociales
  const platformLogos: { [key: string]: string } = {
    facebook: "/img/redesSociales/facebook.png",
    instagram: "/img/redesSociales/instagram.png",
    tiktok: "/img/redesSociales/tiktok.png",
    twitter: "/img/redesSociales/twitter.png",
  };
  const [redesSociales, setRedesSociales] = useState<RedSocial[]>(
    (datosPerfilProfesional?.redes_sociales ?? []).map((redSocial) => ({
      nombre: redSocial.nombre,
      enlace: redSocial.enlace,
      iconPath: platformLogos[redSocial.nombre ?? ""] || "",
    })),
  );
  const handleredesSociales = (updated: RedSocial[]) =>
    setRedesSociales(updated);

  // Mensaje o descripción del perfil
  const [mensaje, setMensaje] = useState<string>(
    datosPerfilProfesional?.descripcion ?? "",
  );

  // Estado y gestión de imágenes de trabajos
  const initialImages: ImagenesTrabajos[] = (
    datosPerfilProfesional?.imagenes_trabajos ?? []
  )
    .map((img) => {
      const url = Array.isArray(img.imagen_trabajo_realizado)
        ? img.imagen_trabajo_realizado[0] || ""
        : img.imagen_trabajo_realizado || "";
      return {
        imagen_trabajo_id: img.imagen_trabajo_id,
        imagen_trabajo_realizado: url,
      };
    })
    .filter((img) => img.imagen_trabajo_realizado !== "");

  const [images, setImages] = useState<ImagenesTrabajos[]>(initialImages);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [deletedImages, setDeletedImages] = useState<ImagenesTrabajos[]>([]);
  const [, setLogoURLs] = useState<string[]>([]);

  // Genera URLs de preview para archivos pendientes
  useEffect(() => {
    const urls = pendingFiles.map((file) => URL.createObjectURL(file));

    return () => urls.forEach(URL.revokeObjectURL);
  }, [pendingFiles]);

  // Genera URL de preview para el logo
  useEffect(() => {
    const urls = logoPendingFiles.map((file) => URL.createObjectURL(file));
    setLogoURLs(urls);
    setLogoPreview(urls[0]);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [logoPendingFiles]);

  useEffect(() => {
    if (!usuarioId) return;

    const fetchUsuario = async () => {
      const res = await safeFetch(`/api/usuario/${usuarioId}`);
      const data = await res.json();
      setUsuario(data);
    };

    fetchUsuario();
  }, [usuarioId]);

  // Inicializa estado si cambian los datos del perfil
  useEffect(() => {
    if (!datosPerfilProfesional) return;

    setFormEditarPerfil({
      perfil_profesional_id: datosPerfilProfesional.perfil_profesional_id,
      usuario_id: datosPerfilProfesional.usuario_id,
      profesion_id: datosPerfilProfesional.profesion_id,
      profesional_id: datosPerfilProfesional.profesional_id,

      nombre_empresa: datosPerfilProfesional.nombre_empresa,
      nombre_comercial: datosPerfilProfesional.nombre_comercial || "",
      dniNie: datosPerfilProfesional.dniNie,
      cif: datosPerfilProfesional.cif,
      correo_perfil: datosPerfilProfesional.correo_perfil,
      url_web: datosPerfilProfesional.url_web,

      // 👇 DATOS DEL AFILIADO
      codigo_afiliado: datosPerfilProfesional?.codigo_afiliado,
      puntos: datosPerfilProfesional.puntos ?? 0,

      direccion: {
        calle: datosPerfilProfesional.direccion?.calle || "",
        localidad: datosPerfilProfesional.direccion?.localidad || "",
        provincia: datosPerfilProfesional.direccion?.provincia || "",
        comunidad: datosPerfilProfesional.direccion?.comunidad || "",
        pais: "España",
        codigo_postal: datosPerfilProfesional.direccion?.codigo_postal || 0,
      },
    });

    setPhones(
      (datosPerfilProfesional.telefonos ?? []).map((p) => ({
        telefono_id: p.telefono_id,
        numero_telefono: p.numero_telefono,
      })),
    );

    setRedesSociales(
      (datosPerfilProfesional.redes_sociales ?? []).map((rs) => {
        const nombre = (rs.nombre ?? "").toLowerCase();

        return {
          nombre: rs.nombre,
          enlace: rs.enlace,
          iconPath: platformLogos[nombre] || "",
        };
      }),
    );

    setMensaje(datosPerfilProfesional.descripcion ?? "");
  }, [datosPerfilProfesional]);

  // Función para añadir imágenes de trabajos
  const handleAddImage = (file: File) => {
    if (images.length + pendingFiles.length >= 12) {
      alert("No puedes subir más de 12 imágenes.");
      return;
    }
    if (pendingFiles.some((f) => f.name === file.name)) {
      alert("Esta imagen ya fue añadida.");
      return;
    }
    setPendingFiles((prev) => [...prev, file]);
  };

  // Función para eliminar imágenes de trabajos
  const handleRemoveImage = (index: number) => {
    if (index < images.length) {
      const toDelete = images[index];
      setImages((prev) => prev.filter((_, i) => i !== index));
      setDeletedImages((prev) => [...prev, toDelete]);
    } else {
      const idx = index - images.length;
      setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  // Función para eliminar logo
  const handleRemoveLogo = () => {
    // Solo limpia el estado local y marca como eliminado
    setLogoDeleted(true);
    setLogoPreview(undefined);
    setLogoPendingFiles([]);
    setCurrentLogoPath(undefined);
    // No tocamos initialLogoPath todavía, lo dejamos para el confirm
  };

  // Función para enviar el formulario y actualizar el perfil
  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación
    if (!validateForm()) {
      const faltantes: string[] = [];
      const dir = formEditarPerfil.direccion;
      if (!formEditarPerfil.nombre_empresa) faltantes.push("razonSocial");
      if (!dir?.calle) faltantes.push("Calle");
      if (!dir?.localidad) faltantes.push("Localidad");
      if (!dir?.provincia) faltantes.push("Provincia");
      if (!dir?.pais) faltantes.push("País");
      if (!dir?.codigo_postal) faltantes.push("Código Postal");

      setError(`Campos obligatorios faltantes: ${faltantes.join(", ")}`);
      return;
    }

    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No hay token de autenticación");

      let finalLogoPath = initialLogoPath.current;

      // Elimina logo anterior si fue borrado
      // Elimina logo anterior si fue borrado
      if (logoDeleted && finalLogoPath) {
        await eliminarImagenEnServidor(finalLogoPath, "imgLogos");
        finalLogoPath = undefined;
      }

      // Sube nuevo logo si hay archivos pendientes
      if (logoPendingFiles.length) {
        const subida = await subirLogo(logoPendingFiles[0], perfilId);

        finalLogoPath = subida.url;

        // Elimina logo anterior si no fue borrado
        if (initialLogoPath.current && !logoDeleted) {
          await eliminarImagenEnServidor(initialLogoPath.current, "imgLogos");
        }

        initialLogoPath.current = finalLogoPath;
        setCurrentLogoPath(finalLogoPath);
      }

      // Elimina imágenes borradas
      for (const img of deletedImages) {
        if (img.imagen_trabajo_realizado) {
          await eliminarImagenEnServidor(
            img.imagen_trabajo_realizado,
            "profesionales",
          );
        }
      }

      // Sube imágenes pendientes
      const uploadedPaths: ImagenesTrabajos[] = [];

      for (const file of pendingFiles) {
        const res = await subirImagenTrabajo(file, perfilId);

        uploadedPaths.push({
          imagen_trabajo_realizado: res.imagen_trabajo_realizado,
        });
      }

      // Combina imágenes finales
      const finalImages = [
        ...images.filter(
          (img) =>
            !deletedImages.some(
              (d) =>
                d.imagen_trabajo_realizado === img.imagen_trabajo_realizado,
            ),
        ),
        ...uploadedPaths,
      ];

      // Envío al backend
      const res = await safeFetch(`/api/editaDatosProfesional/editarCampos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formEditarPerfil,
          descripcion: mensaje,
          foto_perfil: finalLogoPath,
          redes_sociales: redesSociales,
          telefonos: phones,
          imagenes_trabajos: finalImages,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.message || "Error al guardar datos");
      }

      // Mensaje de éxito
      setSuccessMessage("¡Cambios realizados con éxito!");
      setTimeout(() => setSuccessMessage(null), 3000);

      // Reset de estados locales
      setImages(finalImages);
      setDeletedImages([]);
      setPendingFiles([]);
      setLogoDeleted(false);
      setLogoPendingFiles([]);
      initialLogoPath.current = finalLogoPath;
      setCurrentLogoPath(finalLogoPath);
      setLogoPreview(undefined);
    } catch (err: any) {
      console.error("Error al confirmar:", err);
      setError(err.message || "Error inesperado");
    }
  };

  // 🔹 1️⃣ Obtener horarios al montar
  useEffect(() => {
    const fetchHorarios = async () => {
      try {
        const res = await safeFetch(`/api/horarios/${perfilId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.message || "Error al obtener horarios");
        }

        const data: Horario[] = await res.json();
        setHorarios(data);
      } catch (err: any) {
        console.error(err.message);
        setError("No se pudieron cargar los horarios");
      }
    };

    fetchHorarios();
  }, [perfilId, token]);

  // 🔹 2️⃣ Guardar horarios editados desde popup
  const handleHorariosChange = async (newHorarios: Horario[]) => {
    try {
      const res = await safeFetch(`/api/horarios/${perfilId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newHorarios), // ✅ enviar array directamente
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.message || "Error al guardar horarios");
      }

      setHorarios(newHorarios);
      setSuccessMessage("Horarios actualizados con éxito");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error(err.message);
      setError(err.message || "Error al guardar horarios");
    }
  };

  /* Verificar si existe la landing */
  useEffect(() => {
    const comprobarLanding = async () => {
      try {
        const res = await safeFetch(`/api/crearLanding/${perfilId}/existe`);

        const data = await res.json();

        setLandingCreada(data.existe);
      } catch (err) {
        console.error(err);
      }
    };

    if (perfilId) {
      comprobarLanding();
    }
  }, [perfilId]);

  const createLanding = async () => {
    if (landingCreada) return;

    try {
      const res = await safeFetch(`/api/crearLanding/${perfilId}/create`, {
        method: "POST",
      });

      const data = await res.json();
      console.log("Existe landing:", data);

      setAlerta({
        mensaje: "Landing creada correctamente",
        type: "success",
      });
    } catch (err) {
      setAlerta({
        mensaje: "Error creando landing",
        type: "error",
      });
    }
  };

  const ensureSections = async () => {
    const res = await safeFetch(`/api/landing/${perfilId}/sections`);
    const data = await res.json();

    const hero = data.find((s: any) => s.type === "hero");

    if (!hero) {
      await safeFetch(`/api/landing/${perfilId}/section`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "hero",
          props: {
            title: "",
            subtitle: "",
            items: [],
          },
        }),
      });
    }
  };

  useEffect(() => {
    const fetchEstado = async () => {
      const res = await safeFetch(`/api/crearLanding/${perfilId}/estado`);
      if (!res.ok) return;

      const data = await res.json();
      setLandingPublica(Boolean(data.publica));
    };

    if (perfilId) fetchEstado();
  }, [perfilId]);

  const toggleLanding = async () => {
    const next = !landingPublica;

    // optimista
    setLandingPublica(next);

    try {
      const res = await safeFetch(`/api/crearLanding/${perfilId}/estado`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publica: next }),
      });

      if (!res.ok) throw new Error();
    } catch (err) {
      // rollback real
      setLandingPublica(!next);
    }
  };

  return (
    <form onSubmit={handleConfirm}>
      {/* Mensaje de error */}
      {error && <p className="text-red-500">{error}</p>}

      {/* Mensaje de éxito */}
      {successMessage && (
        <div className="fixed items-center left-1/2 transform -translate-x-1/2 bg-blue-500 text-white p-4 rounded-md shadow-md">
          {successMessage}
        </div>
      )}

      {/* Título y botón confirmar */}
      <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4 w-full">
        {/* IZQUIERDA — TÍTULO */}
        <div className="flex justify-center md:justify-start text-zinc-800">
          <h1 className="text-2xl md:text-xl lg:text-3xl font-bold">
            Edita tus datos
          </h1>
        </div>

        {/* CENTRO — BOTÓN EDITAR LANDING */}
        <div className="flex justify-center">
          <ButtonPopup
            type="button"
            onClick={async () => {
              if (!landingCreada) {
                await createLanding();
                setLandingCreada(true);
              }

              await ensureSections();

              navigate(
                `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
              );
            }}
          >
            <span className={landingCreada ? "text-green-600" : "text-red-600"}>
              {landingCreada ? "Editar Landing" : "Crear Landing"}
            </span>
          </ButtonPopup>
        </div>

        <div className="flex justify-center">
          {landingCreada ? (
            <ButtonPopup
              type="button"
              onClick={toggleLanding}
              className={`
                flex items-center gap-3 px-4 py-2 rounded-full border
                transition-all duration-300
                ${
                  landingPublica
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "bg-red-100 border-red-500 text-red-700"
                }
            `}
            >
              <span
                className={`
                    w-3 h-3 rounded-full transition-all duration-300
                    ${landingPublica ? "bg-green-600" : "bg-red-600"}
                `}
              />

              <span>
                {landingPublica ? "Landing pública" : "Landing privada"}
              </span>
            </ButtonPopup>
          ) : (
            // 👇 IMPORTANTE: ocupa espacio pero no muestra nada
            <div className="w-[160px] h-[40px]" />
          )}
        </div>

        {/* DERECHA — EDITAR HORARIOS */}
        <div className="flex justify-center md:justify-end">
          <EditarHorariosProfesionalForm
            horarios={horarios}
            onHorariosChange={handleHorariosChange}
          />
        </div>
      </div>

      {/* Formulario de datos básicos */}
      <EditaPerfilForm
        formEditarPerfil={formEditarPerfil}
        setFormEditarPerfil={setFormEditarPerfil}
        usuario={usuario ?? undefined}
      />

      {/* Sección de teléfonos, redes, imágenes y logo */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="grid xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 xs:col-span-2 sm:col-span-2 md:col-span-1">
          {/* Teléfonos */}
          <div className="xs:col-span-1 sm:col-span-1 md:col-span-2">
            <EditarPerfilTelefono
              phones={phones}
              onPhoneChange={handlePhoneChange}
            />
          </div>

          {/* Redes sociales */}
          <div className="xs:col-span-1 sm:col-span-1 md:col-span-2">
            <EditarPerfilRedesSociales
              redesSociales={redesSociales}
              platformLogos={PLATFORM_LOGOS}
              onRedesSocialesChange={handleredesSociales}
            />
          </div>

          {/* Imágenes de trabajos */}
          <div className="xs:col-span-1 sm:col-span-1 md:col-span-2">
            <EditaImagesProfesional
              images={images}
              onAddImage={handleAddImage}
              onRemoveImage={handleRemoveImage}
              perfil_profesional_id={
                datosPerfilProfesional?.perfil_profesional_id!
              }
              pendingFiles={pendingFiles}
            />
          </div>

          {/* Logo */}
          <div className="xs:col-span-1 sm:col-span-1 md:col-span-2">
            <EditarPerfilLogoForm
              logoName={currentLogoPath}
              logoPreview={logoPreview}
              onAdd={(previewUrl, file) => {
                setLogoPendingFiles([file]);
                setLogoPreview(previewUrl);
              }}
              onRemove={handleRemoveLogo}
              perfil_profesional_id={
                datosPerfilProfesional?.perfil_profesional_id!
              }
              token={token}
            />
          </div>
        </div>

        {/* Textarea de descripción */}
        <div className="rounded-md xs:col-span-2 md:col-span-1 lg:col-span-1 bg-white dark:text-zinc-900">
          <TextareaComun
            value={mensaje}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMensaje(e.target.value)
            }
            className="bg-zinc-200"
          />
        </div>
      </div>

      <BotonTres
        type="submit"
        className="w-full mt-2 h-8 md:col-end-5 md:col-span-1"
      >
        Confirmar datos
      </BotonTres>

      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
    </form>
  );
};

export default EditarFormulario;
